import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService, FinancialAccountRecord } from '../../features/finance/finance-api.service';

interface FinancialAccountOption {
  id: string;
  label: string;
  account: FinancialAccountRecord;
}

@Component({
  standalone: true,
  selector: 'ain-financial-account-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinancialAccountSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t(placeholderKey)"
      [showClear]="true"
      [filter]="true"
      filterBy="label"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector {
      width: 100%;
    }
  `]
})
export class FinancialAccountSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() bookId: string | null = null;
  @Input() placeholderKey = 'finance.accounts.selectPostingAccount';
  @Input() appendTo: 'body' | 'self' = 'body';
  @Output() accountSelected = new EventEmitter<FinancialAccountRecord | null>();

  readonly loading = signal(false);
  readonly options = signal<FinancialAccountOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  ngOnInit(): void {
    this.loadOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookId'] && !changes['bookId'].firstChange) {
      this.loadOptions();
    }
  }

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
    const selected = this.options().find(item => item.id === value)?.account ?? null;
    this.accountSelected.emit(selected);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadOptions(): void {
    this.loading.set(true);
    this.api.listFinancialAccounts(this.bookId ?? undefined).subscribe({
      next: accounts => {
        const byId = new Map(accounts.map(item => [item.id, item]));
        const postingAccounts = accounts
          .filter(item => item.isActive && item.isPostingAllowed)
          .map(item => ({
            id: item.id,
            label: this.buildLabel(item, byId),
            account: item
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        this.options.set(postingAccounts);
        if (this.value() && !postingAccounts.some(item => item.id === this.value())) {
          this.onValueChange(null);
        }
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private buildLabel(account: FinancialAccountRecord, byId: Map<string, FinancialAccountRecord>): string {
    const parentNames: string[] = [];
    const seen = new Set<string>();
    let currentParentId = account.parentAccountId ?? null;
    while (currentParentId && !seen.has(currentParentId)) {
      seen.add(currentParentId);
      const parent = byId.get(currentParentId);
      if (!parent) {
        break;
      }

      parentNames.unshift(parent.name);
      currentParentId = parent.parentAccountId ?? null;
    }

    const parentSuffix = parentNames.length > 0 ? ` (${parentNames.join(' / ')})` : '';
    return `${account.code} - ${account.name}${parentSuffix}`;
  }
}
